import { useState } from 'react';
import { addSoldierTaskUpdate } from '../../../db/task-store';
import { QUICK_STATUS_STEPS } from '../../../types/timeline';
import type { TimelineEvent, TimelineEventType } from '../../../types/timeline';
import type { Task } from '../../../types/task';
import type { User } from '../../../types/user';

/** The steps that happen at the destination — the address rides along with them. */
const DESTINATION_STEPS: readonly TimelineEventType[] = [
  'in_transit',
  'arrived_destination',
  'unloading',
];

interface QuickStatusUpdateBarProps {
  user: User;
  task: Task;
  /** The roster record reporting — the store refuses any other. */
  soldierId: string;
  /** The steps already reported, so a tap is never asked for twice. */
  events: readonly TimelineEvent[];
  disabled?: boolean;
  onReported: () => void | Promise<void>;
}

/**
 * The one-touch bar a driver actually uses: six buttons, each one tap, each one
 * a lifecycle step on the task's feed.
 *
 * Nothing is typed here on purpose — a note takes two hands and a stopped
 * vehicle, while "יצאתי לדרך" has to survive a glove and a moving cab. Every
 * tap goes through `addSoldierTaskUpdate()`, so the same guard that governs a
 * typed update governs these: a soldier reports only for themselves, only on a
 * task their own vehicle rides for, and can never close the task from here.
 */
export function QuickStatusUpdateBar({
  user,
  task,
  soldierId,
  events,
  disabled = false,
  onReported,
}: QuickStatusUpdateBarProps) {
  const [busy, setBusy] = useState<TimelineEventType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reported = new Set(events.map((event) => event.eventType));

  const report = async (eventType: TimelineEventType, note: string) => {
    setBusy(eventType);
    setError(null);
    try {
      await addSoldierTaskUpdate(user, task.id, soldierId, note, 'in_progress', {
        eventType,
        locationText: DESTINATION_STEPS.includes(eventType)
          ? task.destinationAddress
          : undefined,
      });
      await onReported();
    } catch (err) {
      setError(`העדכון נכשל — ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-ink/50">עדכון מהיר — לחיצה אחת, ללא הקלדה.</p>
      <div className="grid grid-cols-2 gap-2">
        {QUICK_STATUS_STEPS.map((step) => {
          const done = reported.has(step.eventType);
          const locked = disabled || busy !== null;
          return (
            <button
              key={step.eventType}
              type="button"
              disabled={locked || done}
              onClick={() => void report(step.eventType, step.note)}
              className={[
                'h-14 rounded-2xl border text-sm font-semibold',
                'flex items-center justify-center gap-2 px-2 text-center',
                done
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-line bg-panel text-ink hover:border-brand-300',
                locked && !done ? 'opacity-50 pointer-events-none' : '',
              ].join(' ')}
            >
              <span className="text-lg leading-none" aria-hidden>
                {step.emoji}
              </span>
              <span className="min-w-0 truncate">{step.label}</span>
            </button>
          );
        })}
      </div>

      {disabled ? (
        <p className="text-xs text-ink/40">אין לך הרשאת עדכון סטטוס למשימה זו.</p>
      ) : null}

      {error ? (
        <p className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
